/**
 * Welle 114/119 – Rechnung / Gutschrift (Seite „Rechnungen", Reiter „Rechnung schreiben").
 *
 * Schnellwahl (Objekt, Vertrag, Firma eintippen) bzw. Aussteller → Empfänger → „Vorbelegen": alles kommt aus DB 2
 * (Laschen der Daten.xlsm) und ist frei änderbar. Nummer <Kürzel>-<JJ>-<NNNN> je Rechnungssteller, fest erst beim
 * Stellen. Positionen von Hand, aus Excel einfügen, in Google bearbeiten (eine Lasche je Rechnung) oder aus der
 * Arbeitszeit übernehmen. „Stellen" trägt die Rechnung ins Rechnungsausgangsbuch ein, legt das PDF mit QR-Code in
 * der Dropbox ab und markiert übernommene Arbeitszeit als abgerechnet.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ArbeitszeitAuswahl from "./ArbeitszeitAuswahl";
import { API, zahl, euro, deDatum, json } from "./hilfen";
import "../mietverwaltung/Tab5Bescheinigungen.css";
import "./Rechnungen.css";

const SAETZE = [19, 7, 0, 16];

function summen(positionen) {
  const je = {};
  (positionen || []).forEach((p) => {
    const betrag = Math.round(zahl(p.menge) * zahl(p.preis) * 100) / 100;
    const s = zahl(p.satz);
    je[s] = Math.round(((je[s] || 0) + betrag) * 100) / 100;
  });
  const saetze = Object.entries(je).map(([s, n]) => ({ satz: Number(s), netto: n, ust: Math.round(n * Number(s)) / 100 }))
    .sort((a, b) => b.satz - a.satz);
  const netto = saetze.reduce((a, x) => a + x.netto, 0);
  const ust = saetze.reduce((a, x) => a + x.ust, 0);
  return { saetze, netto, ust, brutto: netto + ust };
}

function Feld({ label, children, breit }) {
  return (
    <label className="mb-feld" style={breit ? { gridColumn: "1 / -1" } : undefined}>
      <span className="mb-label">{label}</span>
      {children}
    </label>
  );
}

// Platzhalter wie im PDF (rechnung._platzhalter) — nur zur Anzeige unter dem Textfeld
function platzhalter(text, f, s) {
  const b = f.bank || {};
  const iban = (b.iban || "").trim(), bank = (b.bank || "").trim();
  const werte = {
    rg_nr: f.rg_nr || "", faellig_am: deDatum(f.faellig_am), datum: deDatum(f.datum), brutto: euro(s.brutto),
    netto: euro(s.netto), iban, bic: b.bic || "", bank, kontoinhaber: b.kontoinhaber || "",
    konto: iban ? `IBAN ${iban}${bank ? ` bei der ${bank}` : ""}` : "(Konto fehlt)",
    leistungszeitraum: [deDatum(f.leistung_von), deDatum(f.leistung_bis)].filter(Boolean).join(" – "),
  };
  return String(text || "").replace(/\{([a-z_]+)\}/g, (m, k) => (k in werte ? werte[k] : m));
}

// Excel-Zeilen (Tab-getrennt) → Positionen: [Pos] Leistung, Menge, Einheit, Einzelpreis, USt %
function ausExcel(text, satzRegel) {
  const raus = [];
  String(text || "").split(/\r?\n/).forEach((zeile) => {
    if (!zeile.trim()) return;
    let z = zeile.split("\t").map((x) => x.trim());
    if (z.length > 1 && /^\d+$/.test(z[0]) && z[1] && zahl(z[1]) === 0 && !/^[\d.,]+$/.test(z[1])) z = z.slice(1);
    let p;
    if (z.length === 1) p = { text: z[0], menge: 1, einheit: "", preis: 0 };
    else if (z.length === 2) p = { text: z[0], menge: 1, einheit: "", preis: zahl(z[1]) };
    else if (z.length === 3) p = { text: z[0], menge: zahl(z[1]) || 1, einheit: "", preis: zahl(z[2]) };
    else p = { text: z[0], menge: zahl(z[1]) || 1, einheit: z[2] || "", preis: zahl(z[3]) };
    let satz = z.length >= 5 && z[4] !== "" ? zahl(z[4].replace("%", "")) : satzRegel;
    if (satz > 0 && satz < 1) satz = Math.round(satz * 10000) / 100;
    if (/^(leistung|bezeichnung|text|pos)/i.test(p.text) && !p.preis) return;     // Kopfzeile mitkopiert
    raus.push({ ...p, satz, quelle: "excel" });
  });
  return raus;
}

export default function RechnungPanel({ mietvertragNr = "", objekt = "", auftrag = null, onBuchGeaendert }) {
  const [ausstellerListe, setAusstellerListe] = useState([]);
  const [typ, setTyp] = useState("Rechnung");
  const [aussteller, setAussteller] = useState("");
  const [empfArt, setEmpfArt] = useState("mietvertrag");
  const [empfId, setEmpfId] = useState("");
  const [empfQ, setEmpfQ] = useState("");
  const [treffer, setTreffer] = useState([]);
  const [schnell, setSchnell] = useState("");
  const [schnellTreffer, setSchnellTreffer] = useState([]);
  const [form, setForm] = useState(null);
  const [pruef, setPruef] = useState(null);
  const [ordner, setOrdner] = useState("");
  const [busy, setBusy] = useState(false);
  const [meldung, setMeldung] = useState(null);
  const [google, setGoogle] = useState(null);
  const [excelOffen, setExcelOffen] = useState(false);
  const [excelText, setExcelText] = useState("");
  const [azOffen, setAzOffen] = useState(false);

  const zeige = (ok, text) => setMeldung({ ok, text });
  const buchGeaendert = () => onBuchGeaendert && onBuchGeaendert();

  useEffect(() => {
    fetch(`${API}/aussteller`).then(json).then((d) => setAusstellerListe(d.aussteller || [])).catch(() => {});
    fetch(`${API}/google/status`).then(json).then(setGoogle).catch(() => setGoogle({ verfuegbar: false, hinweis: "Google-Status nicht abrufbar." }));
  }, []);

  const vorbelegen = useCallback(async (aid = aussteller, art = empfArt, eid = empfId, rgTyp = typ) => {
    if (!aid) { zeige(false, "Bitte einen Aussteller wählen."); return; }
    setBusy(true); setMeldung(null);
    try {
      const q = new URLSearchParams({ aussteller: aid, empfaenger_art: art, empfaenger_id: art === "frei" ? "" : eid, rg_typ: rgTyp });
      const d = await json(await fetch(`${API}/vorbelegung?${q}`));
      setForm(d); setOrdner(""); setPruef(null);
    } catch (e) { zeige(false, String(e.message || e)); }
    setBusy(false);
  }, [aussteller, empfArt, empfId, typ]);

  // Aus Mietverwaltung / URL: Vertrag → Empfänger = Mieter, Aussteller = Objekt des Vertrags (direkt vorbelegt)
  const startRef = useRef("");
  useEffect(() => {
    const key = `${mietvertragNr}|${objekt}`;
    if (key === "|" || startRef.current === key) return;
    startRef.current = key;
    if (mietvertragNr) {
      const aid = mietvertragNr.split("-")[0];
      setEmpfArt("mietvertrag"); setEmpfId(mietvertragNr); setEmpfQ(mietvertragNr); setAussteller(aid);
      vorbelegen(aid, "mietvertrag", mietvertragNr, "Rechnung");
    } else if (objekt) {
      setAussteller(objekt);
    }
  }, [mietvertragNr, objekt, vorbelegen]);

  // Aufträge aus dem Ausgangsbuch: öffnen / duplizieren
  useEffect(() => {
    if (!auftrag) return;
    (async () => {
      setBusy(true); setMeldung(null);
      try {
        if (auftrag.art === "oeffnen") {
          setForm(await json(await fetch(`${API}/${auftrag.id}`)));
        } else if (auftrag.art === "duplizieren") {
          const d = await json(await fetch(`${API}/${auftrag.id}/duplizieren`, { method: "POST" }));
          setForm(d);
          zeige(true, `Vorlage ${d.vorlage_von} übernommen — neuer Entwurf, Nummer wird beim Stellen vergeben.`);
        }
        setOrdner(""); setPruef(null);
        window.scrollTo({ top: 0, behavior: "smooth" });
      } catch (e) { zeige(false, String(e.message || e)); }
      setBusy(false);
    })();
  }, [auftrag]);

  // Empfänger suchen
  useEffect(() => {
    if (empfArt === "frei") { setTreffer([]); return; }
    const t = setTimeout(() => {
      fetch(`${API}/empfaenger?art=${empfArt}&q=${encodeURIComponent(empfQ)}`).then(json)
        .then((d) => setTreffer(d.empfaenger || [])).catch(() => setTreffer([]));
    }, 250);
    return () => clearTimeout(t);
  }, [empfArt, empfQ]);

  // Schnellwahl: „0101", „005-06", „005-0603", „IHY", Name → Objekte/Firmen + Mietverträge
  useEffect(() => {
    const q = schnell.trim();
    if (!q) { setSchnellTreffer([]); return; }
    const t = setTimeout(async () => {
      const norm = (x) => String(x || "").toLowerCase().replace(/[^0-9a-zäöüß]/g, "");
      const qn = norm(q);
      const ohne0 = (x) => x.replace(/^0+(?=.)/, "");
      const aus = ausstellerListe.filter((a) => norm(a.id).startsWith(qn) || (qn.length >= 3 && ohne0(qn).startsWith(ohne0(norm(a.id))))
        || norm(a.label).includes(qn))
        .slice(0, 8).map((a) => ({ ...a, typ: a.art === "objekt" ? "Objekt" : "Firma/Person" }));
      let mv = [];
      try {
        mv = ((await json(await fetch(`${API}/empfaenger?art=mietvertrag&q=${encodeURIComponent(q)}`))).empfaenger || [])
          .slice(0, 12).map((m) => ({ ...m, typ: "Mietvertrag" }));
      } catch (_) { /* nur Vorschlag */ }
      setSchnellTreffer([...mv, ...aus]);
    }, 220);
    return () => clearTimeout(t);
  }, [schnell, ausstellerListe]);

  const schnellWaehlen = (t) => {
    setSchnell(""); setSchnellTreffer([]);
    if (t.art === "mietvertrag") {
      const aid = t.id.split("-")[0];
      setAussteller(aid); setEmpfArt("mietvertrag"); setEmpfId(t.id); setEmpfQ(t.id);
      vorbelegen(aid, "mietvertrag", t.id, typ);
    } else {
      setAussteller(t.id); setEmpfArt("mietvertrag"); setEmpfId(""); setEmpfQ("");
    }
  };

  // Pflichtangaben + Ablageordner + Dateiname + Nummernvorschlag (verzögert nach jeder Änderung)
  const pruefRef = useRef(0);
  useEffect(() => {
    if (!form || form.nur_excel || form.status === "gestellt" || form.status === "storniert") return;
    const nr = ++pruefRef.current;
    const t = setTimeout(async () => {
      try {
        const d = await json(await fetch(`${API}/pruefen`, {
          method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ daten: form }) }));
        if (nr !== pruefRef.current) return;
        setPruef(d);
        if (d.rg_nr_vorschlag) setForm((f) => (f && f.rg_nr_auto && f.rg_nr !== d.rg_nr_vorschlag ? { ...f, rg_nr: d.rg_nr_vorschlag } : f));
      } catch (_) { /* Prüfung ist nur Hinweis */ }
    }, 600);
    return () => clearTimeout(t);
  }, [form]);

  const gesperrt = !!form && (form.status === "gestellt" || form.status === "storniert" || form.nur_excel);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setIn = (gruppe, k, v) => setForm((f) => ({ ...f, [gruppe]: { ...(f[gruppe] || {}), [k]: v } }));
  const setPos = (i, k, v) => setForm((f) => ({ ...f, positionen: f.positionen.map((p, j) => (j === i ? { ...p, [k]: v } : p)) }));
  const posWeg = (i) => setForm((f) => ({ ...f, positionen: f.positionen.filter((_, j) => j !== i) }));
  const leerePos = (p) => !String(p.text || "").trim() && !zahl(p.preis);
  const posDazu = (neu) => setForm((f) => ({ ...f, positionen: [...f.positionen.filter((p) => !leerePos(p)), ...neu] }));

  const s = useMemo(() => summen((form?.positionen || []).filter((p) => !leerePos(p))), [form]);
  const azImFormular = useMemo(() => new Set((form?.positionen || []).flatMap((p) => (p.az || []).map(([t, i]) => `${t}:${i}`))), [form]);

  const vorlageMiete = () => {
    posDazu((form.vorlagen?.miete || []).map((p) => ({ ...p })));
    if (form.vorlagen?.miete_hinweis && !form.steuerhinweis) set("steuerhinweis", form.vorlagen.miete_hinweis);
  };
  const vorlageWallbox = () => {
    const von = deDatum(form.leistung_von), bis = deDatum(form.leistung_bis);
    posDazu([{ text: `Ladestrom Wallbox ${von}–${bis} (Zählerstand alt … / neu …)`, menge: 0, einheit: "kWh", preis: 0, satz: form.vorlagen?.satz_regel ?? 19 }]);
  };
  const excelUebernehmen = () => {
    const neu = ausExcel(excelText, form.vorlagen?.satz_regel ?? 19);
    if (!neu.length) { zeige(false, "Keine Zeilen erkannt — bitte Zeilen aus Excel/Google kopieren (Leistung, Menge, Einheit, Einzelpreis, USt %)."); return; }
    posDazu(neu); setExcelText(""); setExcelOffen(false);
    zeige(true, `${neu.length} Position(en) aus der Zwischenablage übernommen.`);
  };

  const pdfHolen = async (ablegen, daten = form) => {
    const r = await fetch(`${API}/pdf?ablegen=${ablegen ? 1 : 0}`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ daten, ordner: ablegen && ordner.trim() ? ordner.trim() : null }) });
    if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || "PDF fehlgeschlagen");
    return r;
  };

  const vorschau = async () => {
    setBusy(true);
    try {
      const r = await pdfHolen(false);
      const url = URL.createObjectURL(await r.blob());
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { zeige(false, String(e.message || e)); }
    setBusy(false);
  };

  const entwurfSpeichern = async (daten = form) => {
    const d = await json(await fetch(`${API}/entwurf`, {
      method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ daten }) }));
    setForm((f) => ({ ...f, id: d.id }));
    buchGeaendert();
    return d.id;
  };

  const entwurf = async () => {
    setBusy(true);
    try {
      await entwurfSpeichern();
      zeige(true, "Als Entwurf gespeichert (im Rechnungsausgangsbuch, Nummer wird erst beim Stellen vergeben).");
    } catch (e) { zeige(false, String(e.message || e)); }
    setBusy(false);
  };

  const stellen = async () => {
    const warn = pruef?.warnungen || [];
    const frage = `${form.rg_typ} ${form.rg_nr}${form.rg_nr_auto ? " (Nummer wird jetzt fest vergeben)" : ""} über ${euro(s.brutto)} jetzt stellen?\n\n`
      + "Sie wird ins Rechnungsausgangsbuch eingetragen und als PDF abgelegt in:\n"
      + `${ordner.trim() || pruef?.ablage?.pfad || "(Sammelordner)"}`
      + (azImFormular.size ? `\n\n${azImFormular.size} Arbeitszeit-Eintrag/Einträge werden als abgerechnet markiert.` : "")
      + (warn.length ? `\n\nAchtung, noch offen:\n• ${warn.join("\n• ")}` : "");
    if (!window.confirm(frage)) return;
    setBusy(true);
    try {
      const r = await pdfHolen(true);
      const blob = await r.blob();
      const cd = r.headers.get("Content-Disposition") || "";
      const m = cd.match(/filename\*=UTF-8''([^;]+)/);
      const nr = decodeURIComponent(r.headers.get("X-Rechnung-Nr") || "") || form.rg_nr;
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = m ? decodeURIComponent(m[1]) : `${nr}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      const pfad = r.headers.get("X-Ablage-Pfad");
      setForm((f) => ({ ...f, id: Number(r.headers.get("X-Rechnung-Id")) || f.id, status: "gestellt", rg_nr: nr, rg_nr_auto: false }));
      zeige(true, pfad ? `${form.rg_typ} ${nr} gestellt und abgelegt: ${decodeURIComponent(pfad)}`
        : `${form.rg_typ} ${nr} gestellt — Ablage in der Dropbox hat nicht geklappt, PDF wurde heruntergeladen.`);
      buchGeaendert();
    } catch (e) { zeige(false, String(e.message || e)); }
    setBusy(false);
  };

  const duplizieren = async () => {
    if (!form?.id) return;
    setBusy(true);
    try {
      const d = await json(await fetch(`${API}/${form.id}/duplizieren`, { method: "POST" }));
      setForm(d); setOrdner(""); setPruef(null);
      zeige(true, `Vorlage ${d.vorlage_von} übernommen — neuer Entwurf, Nummer wird beim Stellen vergeben.`);
    } catch (e) { zeige(false, String(e.message || e)); }
    setBusy(false);
  };

  // Google: Fenster VOR dem await öffnen (Popup-Blocker), Entwurf vorher speichern
  const inGoogle = async () => {
    const fenster = window.open("about:blank", "_blank");
    setBusy(true);
    try {
      const rid = gesperrt ? form.id : await entwurfSpeichern();
      const d = await json(await fetch(`${API}/${rid}/google-oeffnen`, { method: "POST" }));
      if (fenster) { fenster.opener = null; fenster.location = d.url; } else window.open(d.url, "_blank", "noopener");
      setForm((f) => ({ ...f, google_gid: true }));
      zeige(true, d.neu ? `Lasche „${d.lasche}“ angelegt — Positionen dort bearbeiten, danach „Aus Google einlesen“.`
        : `Lasche „${d.lasche}“ geöffnet (vorhandene Änderungen in Google bleiben).`);
    } catch (e) { if (fenster) fenster.close(); zeige(false, String(e.message || e)); }
    setBusy(false);
  };

  const ausGoogle = async () => {
    setBusy(true);
    try {
      const rid = await entwurfSpeichern();
      const v = await json(await fetch(`${API}/${rid}/google-einlesen?dry_run=1`, { method: "POST" }));
      setBusy(false);
      if (!window.confirm(`Aus Lasche „${v.lasche}“: ${v.neu} neu, ${v.geaendert} geändert, ${v.entfallen} entfallen — `
        + `${v.positionen.length} Position(en), ${euro(v.summen.brutto)} brutto.\n\nPositionen der Rechnung ersetzen?`)) return;
      setBusy(true);
      const d = await json(await fetch(`${API}/${rid}/google-einlesen?dry_run=0`, { method: "POST" }));
      setForm((f) => ({ ...f, positionen: d.positionen }));
      zeige(true, `${d.positionen.length} Position(en) aus Google übernommen.`);
      buchGeaendert();
    } catch (e) { zeige(false, String(e.message || e)); }
    setBusy(false);
  };

  const objekteListe = ausstellerListe.filter((a) => a.art === "objekt");
  const firmenListe = ausstellerListe.filter((a) => a.art === "firma");
  const ziel = pruef?.ablage;
  const googleAus = !google?.verfuegbar;

  return (
    <div className="rg-wrap">
      <div className="mb-sektion">
        <div className="mb-sektion-titel">Neue Rechnung / Gutschrift</div>
        <div className="rg-schnell">
          <Feld label="Schnellwahl — Objekt, Einheit, Vertrag oder Firma eintippen (z. B. 0101, 005-06, 005-0603, IHY)">
            <input className="mb-input" value={schnell} placeholder="eintippen …" autoComplete="off"
              onChange={(e) => setSchnell(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && schnellTreffer[0]) { e.preventDefault(); schnellWaehlen(schnellTreffer[0]); } }} />
          </Feld>
          {schnellTreffer.length > 0 && (
            <div className="rg-schnell-liste">
              {schnellTreffer.map((t) => (
                <button type="button" key={`${t.art}:${t.id}`} className="rg-schnell-treffer" onClick={() => schnellWaehlen(t)}>
                  <span className="rg-schnell-typ">{t.typ}</span>{t.label}
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="rg-start">
          <Feld label="Art">
            <select className="mb-input" value={typ} onChange={(e) => setTyp(e.target.value)}>
              <option>Rechnung</option><option>Gutschrift</option>
            </select>
          </Feld>
          <Feld label="Aussteller (von)">
            <select className="mb-input" value={aussteller} onChange={(e) => setAussteller(e.target.value)}>
              <option value="">— wählen —</option>
              <optgroup label="Objekte">{objekteListe.map((a) => <option key={a.id} value={a.id}>{a.label}</option>)}</optgroup>
              <optgroup label="Firmen & Personen">{firmenListe.map((a) => <option key={a.id} value={a.id}>{a.label}</option>)}</optgroup>
            </select>
          </Feld>
          <Feld label="Empfänger (an)">
            <select className="mb-input" value={empfArt} onChange={(e) => { setEmpfArt(e.target.value); setEmpfId(""); setEmpfQ(""); }}>
              <option value="mietvertrag">Mieter (Mietvertrag)</option>
              <option value="objekt">Objekt (Eigentümer)</option>
              <option value="adresse">Adresse — Firma, Kreditor, Debitor …</option>
              <option value="frei">frei eingeben</option>
            </select>
          </Feld>
          {empfArt !== "frei" && (
            <Feld label="suchen und wählen">
              <div className="rg-suche">
                <input className="mb-input" value={empfQ} placeholder="Nummer, Name, Suchbegriff …"
                  onChange={(e) => setEmpfQ(e.target.value)} />
                <select className="mb-input" value={empfId} onChange={(e) => setEmpfId(e.target.value)}>
                  <option value="">{treffer.length ? `— ${treffer.length} Treffer —` : "— keine Treffer —"}</option>
                  {treffer.map((t) => <option key={t.id} value={t.id}>{t.gruppe ? `${t.gruppe} · ` : ""}{t.label}</option>)}
                </select>
              </div>
            </Feld>
          )}
          <div className="rg-start-knopf">
            <button className="mb-btn primary" disabled={busy || !aussteller || (empfArt !== "frei" && !empfId)} onClick={() => vorbelegen()}>
              Vorbelegen
            </button>
          </div>
        </div>
        <p className="rg-klein">Alles wird aus DB 2 vorbelegt (Objekte, Adressen, Banken, Mietverträge, Mieten) und ist danach frei änderbar.
          Nummer: <b>Kürzel-JJ-NNNN</b> — ein Kreis je Rechnungssteller (Objekte zählen im Kreis ihres Eigentümers), fest vergeben erst beim Stellen.
          Eine alte Rechnung als Vorlage: im Reiter „Ausgangsbuch“ auf „duplizieren“.</p>
      </div>

      {meldung && <div className={`mb-meldung ${meldung.ok ? "ok" : "fehler"}`}>{meldung.text}</div>}

      {form && (
        <>
          {gesperrt && (
            <div className="mb-hinweis">
              {form.nur_excel ? "Diese Zeile stammt aus der Excel (ohne Formular) — nur ansehen."
                : `${form.rg_typ} ${form.rg_nr} ist ${form.status === "storniert" ? "storniert" : "gestellt"} — nicht mehr änderbar.`}
              {form.pdf_pfad ? ` Abgelegt: ${form.pdf_pfad}` : ""}
              {" "}{!form.nur_excel && <button className="mb-link" onClick={duplizieren}>⧉ duplizieren (als Vorlage)</button>}
              {" "}<button className="mb-link" onClick={() => setForm(null)}>schließen</button>
            </div>
          )}
          {form.vorlage_von && !form.id && (
            <div className="mb-hinweis">Neuer Entwurf nach Vorlage <b>{form.vorlage_von}</b> — Empfänger, Texte und Positionen prüfen.</div>
          )}
          <fieldset className="rg-fieldset" disabled={gesperrt}>
            <div className="rg-zwei">
              <div className="mb-sektion">
                <div className="mb-sektion-titel">{form.rg_typ} · Kopf</div>
                <div className="mb-felder">
                  <Feld label={form.rg_nr_auto ? `${form.rg_typ}snummer (vorläufig — fest beim Stellen)` : `${form.rg_typ}snummer`}>
                    <div className="rg-suche">
                      <input className={`mb-input${form.rg_nr_auto ? " rg-vorlaeufig" : ""}`} value={form.rg_nr || ""}
                        onChange={(e) => setForm((f) => ({ ...f, rg_nr: e.target.value, rg_nr_auto: false }))} />
                      {!form.rg_nr_auto && !gesperrt && form.kreis && (
                        <button type="button" className="mb-btn" title="Nummer wieder automatisch vergeben"
                          onClick={() => setForm((f) => ({ ...f, rg_nr_auto: true }))}>↺ auto</button>
                      )}
                    </div>
                  </Feld>
                  <Feld label="Datum"><input type="date" className="mb-input" value={form.datum || ""} onChange={(e) => set("datum", e.target.value)} /></Feld>
                  <Feld label="Leistung von"><input type="date" className="mb-input" value={form.leistung_von || ""} onChange={(e) => set("leistung_von", e.target.value)} /></Feld>
                  <Feld label="Leistung bis"><input type="date" className="mb-input" value={form.leistung_bis || ""} onChange={(e) => set("leistung_bis", e.target.value)} /></Feld>
                  {form.rg_typ !== "Gutschrift" && (
                    <Feld label="Zahlbar bis"><input type="date" className="mb-input" value={form.faellig_am || ""} onChange={(e) => set("faellig_am", e.target.value)} /></Feld>
                  )}
                  <Feld label="Objekt / Einheit"><input className="mb-input" value={form.objekt || ""} onChange={(e) => set("objekt", e.target.value)} /></Feld>
                  <Feld label="Kunden-Nr. (Debitor)"><input className="mb-input" value={form.debitor || ""} onChange={(e) => set("debitor", e.target.value)} /></Feld>
                  <Feld label="Ansprechpartner (leer = nicht drucken)"><input className="mb-input" value={form.ansprechpartner || ""} onChange={(e) => set("ansprechpartner", e.target.value)} /></Feld>
                </div>
              </div>
              <div className="mb-sektion">
                <div className="mb-sektion-titel">Empfänger <span className="mb-badge db">DB</span></div>
                <div className="mb-felder">
                  <Feld label="Anschrift (eine Zeile je Zeile)" breit>
                    <textarea className="mb-input rg-area" rows={5} value={(form.empfaenger?.zeilen || []).join("\n")}
                      onChange={(e) => setIn("empfaenger", "zeilen", e.target.value.split("\n"))} />
                  </Feld>
                  <Feld label="Name (Rechnungsausgangsbuch, Dateiname)" breit>
                    <input className="mb-input" value={form.empfaenger?.name || ""} onChange={(e) => setIn("empfaenger", "name", e.target.value)} />
                  </Feld>
                </div>
              </div>
            </div>

            <div className="rg-zwei">
              <div className="mb-sektion">
                <div className="mb-sektion-titel">Aussteller <span className="mb-badge db">DB</span>{form.kreis ? <span className="rg-kreis">Nummernkreis {form.kreis}</span> : null}</div>
                <div className="mb-felder">
                  {[["name", "Name"], ["strasse", "Straße"], ["plz_ort", "PLZ Ort"], ["telefon", "Telefon"], ["mail", "E-Mail"],
                    ["steuer_nr", "Steuer-Nr."], ["ust_id", "USt-IdNr."], ["vertreten_durch", "vertreten durch (leer = nicht drucken)"]].map(([k, l]) => (
                    <Feld key={k} label={l}><input className="mb-input" value={form.absender?.[k] || ""} onChange={(e) => setIn("absender", k, e.target.value)} /></Feld>
                  ))}
                </div>
              </div>
              <div className="mb-sektion">
                <div className="mb-sektion-titel">Bankverbindung <span className="mb-badge db">DB</span></div>
                <div className="mb-felder">
                  {(form.bank_optionen || []).length > 0 && (
                    <Feld label="Konto aus „Banken“" breit>
                      <select className="mb-input" value={form.bank?.suchbegriff || ""}
                        onChange={(e) => { const b = form.bank_optionen.find((x) => x.suchbegriff === e.target.value); if (b) set("bank", { ...b }); }}>
                        {!form.bank_optionen.some((x) => x.suchbegriff === form.bank?.suchbegriff) && <option value={form.bank?.suchbegriff || ""}>{form.bank?.iban ? `${form.bank.suchbegriff || "eigenes"} · ${form.bank.iban}` : "— wählen —"}</option>}
                        {form.bank_optionen.map((b) => <option key={b.suchbegriff + b.iban} value={b.suchbegriff}>{b.label} · {b.iban}</option>)}
                      </select>
                    </Feld>
                  )}
                  {[["kontoinhaber", "Kontoinhaber"], ["bank", "Bank"], ["iban", "IBAN"], ["bic", "BIC"]].map(([k, l]) => (
                    <Feld key={k} label={l}><input className="mb-input" value={form.bank?.[k] || ""} onChange={(e) => setIn("bank", k, e.target.value)} /></Feld>
                  ))}
                </div>
              </div>
            </div>

            <div className="mb-sektion">
              <div className="mb-sektion-titel">Text</div>
              <div className="mb-felder">
                <Feld label="Betreff" breit><input className="mb-input" value={form.betreff || ""} onChange={(e) => set("betreff", e.target.value)} /></Feld>
                <Feld label="Text vor den Positionen" breit>
                  <textarea className="mb-input rg-area" rows={4} value={form.text_vor || ""} onChange={(e) => set("text_vor", e.target.value)} />
                </Feld>
              </div>
            </div>

            <div className="mb-sektion">
              <div className="mb-sektion-titel">Positionen</div>
              <div className="rg-tabelle-rahmen">
                <table className="rg-tabelle">
                  <thead>
                    <tr><th>#</th><th>Leistung</th><th>Menge</th><th>Einheit</th><th>Einzelpreis netto</th><th>USt %</th><th className="r">Betrag netto</th><th /></tr>
                  </thead>
                  <tbody>
                    {(form.positionen || []).map((p, i) => (
                      <tr key={i}>
                        <td className="rg-nr">{i + 1}</td>
                        <td>
                          <textarea className="mb-input rg-area rg-text" rows={Math.min(4, Math.max(1, Math.ceil(String(p.text || "").length / 62)))} value={p.text || ""} onChange={(e) => setPos(i, "text", e.target.value)} />
                          {(p.az || []).length > 0 && <div className="rg-pos-herkunft">⏱ {p.az.length} Arbeitszeit-Eintrag/Einträge</div>}
                        </td>
                        <td><input className="mb-input rg-zahl" value={p.menge ?? ""} onChange={(e) => setPos(i, "menge", e.target.value)} /></td>
                        <td><input className="mb-input rg-einheit" value={p.einheit || ""} onChange={(e) => setPos(i, "einheit", e.target.value)} /></td>
                        <td><input className="mb-input rg-zahl" value={p.preis ?? ""} onChange={(e) => setPos(i, "preis", e.target.value)} /></td>
                        <td>
                          <select className="mb-input rg-satz" value={zahl(p.satz)} onChange={(e) => setPos(i, "satz", Number(e.target.value))}>
                            {[...new Set([...SAETZE, zahl(p.satz)])].map((x) => <option key={x} value={x}>{x} %</option>)}
                          </select>
                        </td>
                        <td className="r">{euro(zahl(p.menge) * zahl(p.preis))}</td>
                        <td><button type="button" className="mb-link" title="Position entfernen" onClick={() => posWeg(i)}>✕</button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="rg-knoepfe">
                <button type="button" className="mb-btn" onClick={() => posDazu([{ text: "", menge: 1, einheit: "", preis: 0, satz: form.vorlagen?.satz_regel ?? 19 }, ])}>+ Position</button>
                <button type="button" className="mb-btn" onClick={() => setAzOffen(true)} title="Stunden aus der Arbeitszeit (Arbeitsnachweis) als Positionen übernehmen">⏱ Arbeitszeit übernehmen</button>
                <button type="button" className="mb-btn" onClick={() => setExcelOffen((x) => !x)} title="Zeilen aus Excel/Google kopieren und hier einfügen">📋 Aus Excel einfügen</button>
                {(form.vorlagen?.miete || []).length > 0 && (
                  <button type="button" className="mb-btn" onClick={vorlageMiete} title="Nettokaltmiete + Vorauszahlungen aus der Lasche „Mieten“">
                    Miete wie Dauermietrechnung</button>
                )}
                <button type="button" className="mb-btn" onClick={vorlageWallbox}>Wallbox / Strom</button>
                <div className="rg-summen">
                  <div><span>Summe netto</span><b>{euro(s.netto)}</b></div>
                  {s.saetze.filter((x) => x.satz).map((x) => (
                    <div key={x.satz}><span>{x.satz} % USt auf {euro(x.netto)}</span><b>{euro(x.ust)}</b></div>
                  ))}
                  <div className="rg-gesamt"><span>{form.rg_typ === "Gutschrift" ? "Gutschriftsbetrag" : "Rechnungsbetrag"}</span><b>{euro(s.brutto)}</b></div>
                </div>
              </div>
              {excelOffen && (
                <div className="rg-excel">
                  <textarea className="mb-input rg-area" rows={5} value={excelText} autoFocus
                    placeholder={"Zeilen aus Excel/Google hier einfügen (Strg/Cmd+V) — Spalten: Leistung ⇥ Menge ⇥ Einheit ⇥ Einzelpreis ⇥ USt %"}
                    onChange={(e) => setExcelText(e.target.value)} />
                  <div className="rg-knoepfe">
                    <button type="button" className="mb-btn primary" onClick={excelUebernehmen}>Zeilen übernehmen</button>
                    <button type="button" className="mb-btn" onClick={() => { setExcelOffen(false); setExcelText(""); }}>abbrechen</button>
                  </div>
                </div>
              )}
            </div>

            <div className="mb-sektion">
              <div className="mb-sektion-titel">Schluss</div>
              <div className="mb-felder">
                <Feld label="Steuerhinweis (bei 0 % Pflicht)" breit>
                  <div className="rg-suche">
                    <input className="mb-input" value={form.steuerhinweis || ""} onChange={(e) => set("steuerhinweis", e.target.value)} />
                    <button type="button" className="mb-btn" onClick={() => set("steuerhinweis", "Umsatzsteuerfrei gemäß § 4 Nr. 12a UStG (Vermietung und Verpachtung von Grundstücken).")}>§ 4 Nr. 12a</button>
                  </div>
                </Feld>
                <Feld label="Text nach den Positionen — Platzhalter {brutto}, {faellig_am}, {rg_nr}, {konto}, {iban}, {bank}" breit>
                  <textarea className="mb-input rg-area" rows={2} value={form.text_nach || ""} onChange={(e) => set("text_nach", e.target.value)} />
                  <span className="rg-klein">So steht es auf der Rechnung: {platzhalter(form.text_nach, form, s)}</span>
                </Feld>
                <Feld label="Gruß"><input className="mb-input" value={form.gruss || ""} onChange={(e) => set("gruss", e.target.value)} /></Feld>
                <Feld label="Unterschrift"><input className="mb-input" value={form.unterschrift || ""} onChange={(e) => set("unterschrift", e.target.value)} /></Feld>
              </div>
            </div>
          </fieldset>

          {!form.nur_excel && (
            <div className="mb-sektion">
              <div className="mb-sektion-titel">Positionen in Google</div>
              <p className="rg-klein" style={{ marginTop: 0 }}>Eine Google-Tabelle, je Rechnung eine eigene Lasche — dort Positionen schreiben, kopieren
                (auch aus anderen Rechnungen), danach hier einlesen. Eine vorhandene Lasche wird beim Öffnen nie überschrieben.
                {google?.hinweis ? ` ${google.hinweis}` : ""}</p>
              <div className="rg-knoepfe" style={{ marginTop: 0 }}>
                <button type="button" className="mb-btn" disabled={busy || googleAus} onClick={inGoogle}>🌐 In Google bearbeiten</button>
                <button type="button" className="mb-btn" disabled={busy || googleAus || gesperrt || !form.google_gid} onClick={ausGoogle}
                  title={form.google_gid ? "" : "Zuerst „In Google bearbeiten“"}>⟳ Aus Google einlesen</button>
              </div>
            </div>
          )}

          {!gesperrt && (
            <div className="mb-sektion">
              <div className="mb-sektion-titel">Prüfen & ablegen</div>
              {pruef?.warnungen?.length ? (
                <ul className="rg-warn">{pruef.warnungen.map((w) => <li key={w}>{w}</li>)}</ul>
              ) : pruef ? <p className="rg-ok">Pflichtangaben vollständig.</p> : <p className="rg-klein">prüft …</p>}
              {ziel && (
                <div className="rg-ablage">
                  <div><b>Ablage</b> ({ziel.quelle === "pfade" ? `Lasche „Pfade“, Zeile ${ziel.zeile}` : "Sammelordner"}):{" "}
                    <code>{ziel.pfad}</code>{ziel.existiert === false && " — Ordner fehlt, wird angelegt"}</div>
                  {ziel.hinweis && <div className="rg-klein">{ziel.hinweis}</div>}
                  {ziel.vorschlag && (
                    <button type="button" className="mb-link" onClick={() => setOrdner(ziel.vorschlag)}>Vorhandenen Ordner nehmen: {ziel.vorschlag}</button>
                  )}
                  <Feld label="anderer Ordner (leer = wie oben)">
                    <input className="mb-input" value={ordner} placeholder="/Holthey Immobilien/…" onChange={(e) => setOrdner(e.target.value)} />
                  </Feld>
                  <div className="rg-klein">Dateiname: <code>{pruef.dateiname}</code></div>
                </div>
              )}
              <div className="mb-buttons">
                <button className="mb-btn" disabled={busy} onClick={vorschau}>Vorschau (PDF)</button>
                <button className="mb-btn" disabled={busy} onClick={entwurf}>Als Entwurf speichern</button>
                <button className="mb-btn primary" disabled={busy} onClick={stellen}>{busy ? "arbeitet …" : `${form.rg_typ} stellen & ablegen`}</button>
              </div>
            </div>
          )}
        </>
      )}

      {azOffen && form && (
        <ArbeitszeitAuswahl form={form} schonDrin={azImFormular} onClose={() => setAzOffen(false)}
          onUebernehmen={(neu) => { posDazu(neu); setAzOffen(false); zeige(true, `${neu.length} Position(en) aus der Arbeitszeit übernommen.`); }} />
      )}
    </div>
  );
}
